/**
 * AdBannerSystem - Système de bannières publicitaires premium
 * Support: Desktop (skyscraper latéral) + Mobile (horizontal)
 * Modes: Statique, Animé CSS, Carousel rotation
 * 
 * ⚠️ PUBLICITÉS DÉSACTIVÉES TEMPORAIREMENT - Sera activé après mise en prod
 */

// Configuration globale - Mettre à true pour activer les publicités
const ADS_ENABLED = false;

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES ANNONCEURS (Fallback si API indisponible)
// ══════════════════════════════════════════════════════════════════════════════
const FALLBACK_CAMPAIGNS = [
  {
    id: 'monteepilote',
    name: 'Montée Pilote',
    tagline: 'Décolle vers ton rêve',
    description: 'Formation pilote privé & pro',
    url: 'https://monteepilote.com',
    cta: 'Découvrir',
    images: {
      desktop: 'https://images.unsplash.com/photo-1716667282954-37b75707dccd?w=400&h=600&fit=crop',
      mobile: 'https://images.unsplash.com/photo-1758185057969-c8a25917dc19?w=600&h=200&fit=crop',
    },
    colors: {
      primary: '#1E3A5F',
      secondary: '#F97316',
      gradient: 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8a 50%, #1E3A5F 100%)',
      text: '#ffffff',
    },
    animation: 'glow',
    active: true,
    priority: 1,
  },
  {
    id: 'place2paddle',
    name: 'Place 2 Paddle',
    tagline: 'Glisse sur l\'eau',
    description: 'Spots paddle & locations',
    url: 'https://place2paddle.com',
    cta: 'Explorer',
    images: {
      desktop: 'https://images.unsplash.com/photo-1635997504434-b997d9f47c48?w=400&h=600&fit=crop',
      mobile: 'https://images.unsplash.com/photo-1772616983846-a8581a01b6c4?w=600&h=200&fit=crop',
    },
    colors: {
      primary: '#0891B2',
      secondary: '#06B6D4',
      gradient: 'linear-gradient(135deg, #0891B2 0%, #22D3EE 50%, #0891B2 100%)',
      text: '#ffffff',
    },
    animation: 'pulse',
    active: true,
    priority: 2,
  },
];

// Hook pour récupérer les publicités
const useAds = (page = null) => {
  const [ads, setAds] = useState(FALLBACK_CAMPAIGNS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const params = page ? `?page=${page}` : '';
        const response = await axios.get(`${API}/ads/active${params}`);
        if (response.data && response.data.length > 0) {
          setAds(response.data);
        }
      } catch (error) {
        console.log('Using fallback ads');
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, [page]);

  return { ads, loading };
};

// Fonction pour tracker les clics
const trackClick = async (adId) => {
  try {
    await axios.post(`${API}/ads/track/click/${adId}`);
  } catch (error) {
    // Silently fail
  }
};

// Fonction pour tracker les impressions
const trackImpression = async (adId) => {
  try {
    await axios.post(`${API}/ads/track/impression/${adId}`);
  } catch (error) {
    // Silently fail
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// STYLES CSS ANIMATIONS
// ══════════════════════════════════════════════════════════════════════════════
const AdStyles = () => (
  <style>{`
    @keyframes adGlow {
      0%, 100% { 
        box-shadow: 0 0 20px rgba(249, 115, 22, 0.3), 
                    0 4px 20px rgba(0, 0, 0, 0.15);
      }
      50% { 
        box-shadow: 0 0 30px rgba(249, 115, 22, 0.5), 
                    0 4px 30px rgba(0, 0, 0, 0.2);
      }
    }
    
    @keyframes adPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); }
    }
    
    @keyframes adSlideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    
    @keyframes adFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes adShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    
    .ad-banner-glow { animation: adGlow 3s ease-in-out infinite; }
    .ad-banner-pulse { animation: adPulse 4s ease-in-out infinite; }
    .ad-banner-slide { animation: adSlideIn 0.5s ease-out; }
    .ad-banner-fade { animation: adFadeIn 0.5s ease-out; }
    
    .ad-shimmer-overlay {
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,255,255,0.1) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: adShimmer 3s ease-in-out infinite;
    }
    
    .ad-banner-desktop {
      width: 160px;
      min-height: 600px;
    }
    
    .ad-banner-mobile {
      width: 100%;
      height: 120px;
    }
    
    @media (min-width: 1400px) {
      .ad-banner-desktop { width: 200px; }
    }
  `}</style>
);

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT BANNIÈRE INDIVIDUELLE
// ══════════════════════════════════════════════════════════════════════════════
const AdBanner = ({ ad, variant = 'desktop', onClose, showClose = false }) => {
  const isDesktop = variant === 'desktop';
  const animationClass = `ad-banner-${ad.animation || 'glow'}`;
  
  // Track impression on mount
  useEffect(() => {
    if (ad.id) {
      trackImpression(ad.id);
    }
  }, [ad.id]);

  const handleClick = () => {
    if (ad.id) {
      trackClick(ad.id);
    }
  };
  
  return (
    <motion.a
      href={ad.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={handleClick}
      className={`
        relative block overflow-hidden rounded-2xl cursor-pointer
        ${isDesktop ? 'ad-banner-desktop' : 'ad-banner-mobile'}
        ${animationClass}
      `}
      style={{
        background: ad.colors?.gradient || 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8a 100%)',
        textDecoration: 'none',
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Image de fond */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${isDesktop ? ad.images?.desktop : ad.images?.mobile})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.4,
        }}
      />
      
      {/* Overlay gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isDesktop 
            ? 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)'
            : 'linear-gradient(to right, rgba(0,0,0,0.6) 0%, transparent 50%, rgba(0,0,0,0.6) 100%)',
        }}
      />
      
      {/* Shimmer effect */}
      <div className="ad-shimmer-overlay" style={{ position: 'absolute', inset: 0 }} />
      
      {/* Contenu */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: isDesktop ? 'column' : 'row',
          alignItems: isDesktop ? 'center' : 'center',
          justifyContent: isDesktop ? 'flex-end' : 'space-between',
          padding: isDesktop ? '20px 16px' : '12px 16px',
          color: ad.colors.text,
        }}
      >
        {/* Badge "Pub" */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            fontSize: 9,
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.4)',
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Pub
        </div>
        
        {/* Close button */}
        {showClose && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose?.(); }}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={12} color="rgba(255,255,255,0.7)" />
          </button>
        )}
        
        {isDesktop ? (
          /* Layout Desktop - Vertical */
          <>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 800, 
                marginBottom: 4,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}>
                {ad.name}
              </div>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 600, 
                opacity: 0.9,
                color: ad.colors.secondary,
              }}>
                {ad.tagline}
              </div>
              <div style={{ 
                fontSize: 11, 
                opacity: 0.75, 
                marginTop: 6,
                lineHeight: 1.4,
              }}>
                {ad.description}
              </div>
            </div>
            
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 20,
                background: ad.colors.secondary,
                fontSize: 12,
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
            >
              {ad.cta}
              <ExternalLink size={12} />
            </div>
          </>
        ) : (
          /* Layout Mobile - Horizontal */
          <>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: 15, 
                fontWeight: 800, 
                marginBottom: 2,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}>
                {ad.name}
              </div>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                opacity: 0.9,
                color: ad.colors.secondary,
              }}>
                {ad.tagline}
              </div>
            </div>
            
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 14px',
                borderRadius: 16,
                background: ad.colors.secondary,
                fontSize: 11,
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                flexShrink: 0,
              }}
            >
              {ad.cta}
              <ExternalLink size={11} />
            </div>
          </>
        )}
      </div>
    </motion.a>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CAROUSEL DE BANNIÈRES
// ══════════════════════════════════════════════════════════════════════════════
const AdCarousel = ({ ads, variant = 'desktop', rotationInterval = 8000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const nextAd = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  }, [ads.length]);
  
  const prevAd = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
  }, [ads.length]);
  
  useEffect(() => {
    if (isPaused || ads.length <= 1) return;
    
    const timer = setInterval(nextAd, rotationInterval);
    return () => clearInterval(timer);
  }, [isPaused, nextAd, rotationInterval, ads.length]);
  
  if (ads.length === 0) return null;
  
  const isDesktop = variant === 'desktop';
  
  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <AdBanner 
          key={ads[currentIndex].id} 
          ad={ads[currentIndex]} 
          variant={variant} 
        />
      </AnimatePresence>
      
      {/* Navigation dots */}
      {ads.length > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            marginTop: 8,
          }}
        >
          {ads.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              style={{
                width: i === currentIndex ? 16 : 6,
                height: 6,
                borderRadius: 3,
                background: i === currentIndex ? '#F97316' : 'rgba(0,0,0,0.2)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>
      )}
      
      {/* Navigation arrows (desktop only) */}
      {isDesktop && ads.length > 1 && (
        <>
          <button
            onClick={prevAd}
            style={{
              position: 'absolute',
              left: -12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'white',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
            className="group-hover:opacity-100"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={nextAd}
            style={{
              position: 'absolute',
              right: -12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'white',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
            className="group-hover:opacity-100"
          >
            <ChevronRight size={14} />
          </button>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS WRAPPER POUR INTÉGRATION
// ══════════════════════════════════════════════════════════════════════════════

// Bannière latérale Desktop (skyscraper)
export const AdSidebarLeft = ({ className = '', page = null }) => {
  const { ads, loading } = useAds(page);
  
  // Publicités désactivées temporairement
  if (!ADS_ENABLED) return null;
  
  const activeAds = ads.filter(ad => ad.active).sort((a, b) => a.priority - b.priority);
  
  if (loading || activeAds.length === 0) return null;
  
  return (
    <>
      <AdStyles />
      <div 
        className={`hidden 2xl:block fixed z-40 ${className}`}
        style={{
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <AdCarousel ads={activeAds} variant="desktop" />
      </div>
    </>
  );
};

export const AdSidebarRight = ({ className = '', page = null }) => {
  const { ads, loading } = useAds(page);
  
  // Publicités désactivées temporairement
  if (!ADS_ENABLED) return null;
  
  const activeAds = ads.filter(ad => ad.active).sort((a, b) => a.priority - b.priority);
  
  if (loading || activeAds.length === 0) return null;
  
  return (
    <>
      <AdStyles />
      <div 
        className={`hidden 2xl:block fixed z-40 ${className}`}
        style={{
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <AdCarousel ads={[...activeAds].reverse()} variant="desktop" />
      </div>
    </>
  );
};

// Bannière horizontale Mobile (entre contenus)
export const AdBannerMobile = ({ className = '', style = {}, page = null }) => {
  const { ads, loading } = useAds(page);
  
  // Publicités désactivées temporairement
  if (!ADS_ENABLED) return null;
  
  const activeAds = ads.filter(ad => ad.active).sort((a, b) => a.priority - b.priority);
  
  if (loading || activeAds.length === 0) return null;
  
  return (
    <>
      <AdStyles />
      <div 
        className={`block 2xl:hidden px-4 py-3 ${className}`}
        style={style}
      >
        <AdCarousel ads={activeAds} variant="mobile" />
      </div>
    </>
  );
};

// Bannière inline (dans le contenu)
export const AdBannerInline = ({ variant = 'mobile', maxAds = 1, className = '', page = null }) => {
  const { ads, loading } = useAds(page);
  
  // Publicités désactivées temporairement
  if (!ADS_ENABLED) return null;
  
  const activeAds = ads.filter(ad => ad.active).sort((a, b) => a.priority - b.priority).slice(0, maxAds);
  
  if (loading || activeAds.length === 0) return null;
  
  return (
    <>
      <AdStyles />
      <div className={`${className}`}>
        {maxAds === 1 && activeAds[0] ? (
          <AdBanner ad={activeAds[0]} variant={variant} />
        ) : (
          <AdCarousel ads={activeAds} variant={variant} />
        )}
      </div>
    </>
  );
};

// Export hook et fallback pour usage externe
export { useAds, FALLBACK_CAMPAIGNS };

export default { AdSidebarLeft, AdSidebarRight, AdBannerMobile, AdBannerInline };
